import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ConfirmDialog } from "@/components/tableUtils";
import { UserPlus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { username: "", password: "", role: "it", email: "", nip: "" };

export default function Users() {
  const { user: current } = useAuth();
  const [users, setUsers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY);
  const [confirmUser, setConfirmUser] = useState(null);

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.username || !form.password) {
      toast.error("Username dan password wajib diisi");
      return;
    }
    try {
      await api.post("/users", { ...form, email: form.email || null, nip: form.nip || null });
      toast.success("Pengguna ditambahkan");
      setDialogOpen(false);
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const openEdit = (u) => {
    setEditing(u);
    setEditForm({ username: u.username, password: "", role: u.role, email: u.email || "", nip: u.nip || "" });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editForm.username) {
      toast.error("Username wajib diisi");
      return;
    }
    try {
      const payload = { username: editForm.username, role: editForm.role, email: editForm.email || null, nip: editForm.nip || null };
      if (editForm.password) payload.password = editForm.password;
      await api.put(`/users/${editing.id}`, payload);
      toast.success("Pengguna diperbarui");
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (u) => {
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(`Pengguna ${u.username} dihapus`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="users-page" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight">Manajemen User</h1>
          <p className="text-sm text-muted-foreground">Tambah atau hapus akun pengguna SIPAT.</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} data-testid="tambah-user-button">
          <UserPlus className="w-4 h-4 mr-2" /> Tambah User
        </Button>
      </div>

      <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Username</TableHead>
              <TableHead>NIP</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead className="text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.username}`}>
                <TableCell className="font-medium">{u.username}</TableCell>
                <TableCell>{u.nip || "—"}</TableCell>
                <TableCell>{u.email || "—"}</TableCell>
                <TableCell>
                  <Badge variant={u.role === "it" ? "secondary" : "outline"} className="uppercase text-[10px]" data-testid={`user-role-${u.username}`}>
                    {u.role === "it" ? "Staff IT" : "Pimpinan"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(u)} title="Edit" data-testid={`edit-user-${u.username}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    {u.id !== current.id && (
                      <Button variant="ghost" size="icon" onClick={() => setConfirmUser(u)} title="Hapus" data-testid={`delete-user-${u.username}`}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle>Tambah User</DialogTitle>
            <DialogDescription>Buat akun pengguna baru untuk SIPAT.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Username</Label>
              <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="user-form-username" />
            </div>
            <div className="space-y-2">
              <Label>NIP</Label>
              <Input value={form.nip} onChange={(e) => setForm({ ...form, nip: e.target.value })} data-testid="user-form-nip" />
            </div>
            <div className="space-y-2">
              <Label>Password</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-form-password" />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-form-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="it">Staff IT</SelectItem>
                  <SelectItem value="pimpinan">Pimpinan</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Email (opsional)</Label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-form-email" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="user-form-cancel">Batal</Button>
            <Button onClick={save} data-testid="user-form-save">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="user-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>Perbarui data pengguna. Kosongkan password jika tidak diubah.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Username</Label>
              <Input value={editForm.username} onChange={(e) => setEditForm({ ...editForm, username: e.target.value })} data-testid="user-edit-username" />
            </div>
            <div className="space-y-2">
              <Label>NIP</Label>
              <Input value={editForm.nip} onChange={(e) => setEditForm({ ...editForm, nip: e.target.value })} data-testid="user-edit-nip" />
            </div>
            <div className="space-y-2">
              <Label>Password Baru (opsional)</Label>
              <Input type="password" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} placeholder="Kosongkan jika tidak diubah" data-testid="user-edit-password" />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                <SelectTrigger data-testid="user-edit-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="it">Staff IT</SelectItem>
                  <SelectItem value="pimpinan">Pimpinan</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Email (opsional)</Label>
              <Input value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} data-testid="user-edit-email" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} data-testid="user-edit-cancel">Batal</Button>
            <Button onClick={saveEdit} data-testid="user-edit-save">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirmUser}
        onOpenChange={(o) => !o && setConfirmUser(null)}
        onConfirm={() => { remove(confirmUser); setConfirmUser(null); }}
        title="Apakah Anda yakin?"
        description={`Pengguna "${confirmUser?.username}" akan dihapus permanen.`}
        confirmLabel="Ya, Hapus"
        testid="confirm-delete-user"
      />
    </div>
  );
}
